'use client';

import { ChevronDown } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ModelPanel,
  RatioPanel,
  TypePanel,
  useGeneratorBarState,
} from '@repo/mobile-workspace';
import { apiHooks } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useAuthDialog } from './AuthDialog';

type ActivePanel = 'type' | 'model' | 'ratio' | null;

const TYPE_LABEL: Record<'image' | 'video' | 'music' | 'chat', string> = {
  image: 'Изображение',
  video: 'Видео',
  music: 'Музыка',
  chat: 'Чат',
};

export function GeneratorPanel() {
  const router = useRouter();
  const { authed } = useAuth();
  const { openDialog } = useAuthDialog();
  const { data: models } = useQuery({
    queryKey: ['models'],
    queryFn: apiHooks.getModels,
  });

  const state = useGeneratorBarState({
    activeChatId: null,
    models,
    hasImages: false,
    onPreloadInputImage: () => {},
    formatPrice: (value) => `${value} ₽`,
  });

  const [openPanel, setOpenPanel] = useState<ActivePanel>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!openPanel) return;
    const handler = (event: MouseEvent) => {
      const target = event.target as Element | null;
      if (rootRef.current?.contains(target)) return;
      if (target?.closest?.('.gen-popover')) return;
      setOpenPanel(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openPanel]);

  const togglePanel = (panel: ActivePanel) =>
    setOpenPanel((prev) => (prev === panel ? null : panel));

  const handleSubmit = () => {
    if (authed) {
      router.push('/app');
      return;
    }
    openDialog();
  };

  const currentModelName = state.currentModel?.name ?? 'Модель';
  const currentRatioLabel = state.selectedRatio
    ? state.selectedRatio === 'auto'
      ? 'Авто'
      : state.selectedRatio
    : 'Размер';

  return (
    <div
      ref={rootRef}
      className="relative rounded-[26px] bg-[#f0f0f0]/95 p-4 shadow-[0_24px_60px_-30px_rgba(0,0,0,0.6)] backdrop-blur md:max-w-[758px] md:p-5"
    >
      <textarea
        value={state.prompt}
        onChange={(e) => state.setPrompt(e.target.value)}
        rows={2}
        placeholder="Опишите, что хотите получить в результате, или сгенерируйте описание"
        className="w-full resize-none bg-transparent px-1 py-2 text-base text-[#2a2c31] placeholder:text-[#2a2c31]/70 focus:outline-none md:text-lg"
        style={{ letterSpacing: '-0.4px' }}
      />

      <div className="my-2 h-px bg-[#c7c7cc]" />

      <div className="flex items-center justify-between gap-2 pt-1">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <ChipWithPopover
            isOpen={openPanel === 'type'}
            popover={
              <TypePanel
                selected={state.generationType}
                onSelect={(type) => {
                  state.handleSelectType(type);
                  setOpenPanel(null);
                }}
              />
            }
            popoverWidth={300}
          >
            <LightChip
              label={TYPE_LABEL[state.generationType]}
              active={openPanel === 'type'}
              onClick={() => togglePanel('type')}
            />
          </ChipWithPopover>

          {state.filteredModels.length > 0 && (
            <ChipWithPopover
              isOpen={openPanel === 'model'}
              popover={
                <ModelPanel
                  models={state.filteredModels}
                  selectedSlug={state.selectedModelSlug}
                  onSelect={(slug) => {
                    state.handleSelectModel(slug);
                    setOpenPanel(null);
                  }}
                />
              }
              popoverWidth={320}
            >
              <LightChip
                label={currentModelName}
                active={openPanel === 'model'}
                onClick={() => togglePanel('model')}
              />
            </ChipWithPopover>
          )}

          {state.availableRatios.length > 0 && (
            <ChipWithPopover
              isOpen={openPanel === 'ratio'}
              popover={
                <RatioPanel
                  ratios={state.availableRatios}
                  selected={state.selectedRatio}
                  onSelect={state.handleSelectRatio}
                  centered
                />
              }
              // Same width formula the real desktop dock uses — fits ~6
              // ratio chips at min, expands for models with more (seedance:
              // 7, grok-video: 8) so the inner row never needs horizontal
              // scroll under the chips.
              popoverWidth={Math.min(720, Math.max(360, state.availableRatios.length * 55 + 50))}
            >
              <LightChip
                label={currentRatioLabel}
                active={openPanel === 'ratio'}
                onClick={() => togglePanel('ratio')}
              />
            </ChipWithPopover>
          )}
        </div>

        <button
          type="button"
          onClick={handleSubmit}
          aria-label="Отправить"
          className="flex h-[45px] w-[45px] shrink-0 items-center justify-center rounded-full bg-[#d3573b] transition-colors hover:bg-[#ef6444]"
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/logo-symbol.svg"
            alt=""
            width={30}
            height={28}
            style={{ filter: 'brightness(0) invert(1)' }}
          />
        </button>
      </div>

    </div>
  );
}

function ChipWithPopover({
  children,
  popover,
  isOpen,
  popoverWidth,
}: {
  children: React.ReactNode;
  popover: React.ReactNode;
  isOpen: boolean;
  /** Inline width in pixels (so callers can compute it at runtime — a
   * Tailwind class can't carry a dynamic value). */
  popoverWidth: number;
}) {
  const chipRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  useLayoutEffect(() => {
    if (!isOpen || !chipRef.current) return;
    const update = () => {
      const rect = chipRef.current!.getBoundingClientRect();
      setCoords({ top: rect.top + rect.height / 2, left: rect.right + 8 });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [isOpen]);

  return (
    <div ref={chipRef} className="relative">
      {children}
      {mounted && isOpen && coords &&
        createPortal(
          <div
            className="gen-popover fixed z-[60] -translate-y-1/2"
            style={{ top: coords.top, left: coords.left, width: popoverWidth }}
          >
            {popover}
          </div>,
          document.body,
        )}
    </div>
  );
}

function LightChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'inline-flex h-[41px] items-center gap-1 rounded-[20px] pl-4 pr-3 text-sm font-medium transition-colors ' +
        (active
          ? 'bg-[#2a2c31] text-white'
          : 'bg-[#d1d1d6] text-[#2a2c31] hover:bg-[#c7c7cc]')
      }
    >
      <span className="max-w-[180px] truncate">{label}</span>
      <ChevronDown
        size={14}
        className={'transition-transform duration-200 ' + (active ? 'rotate-180' : '')}
      />
    </button>
  );
}
